import { useEffect, useState, useCallback } from "react";
import { api, apiError, ROLE_LABELS, STATUS_LABELS, BACKEND_URL } from "@/lib/api";
import { RoleBadge, Avatar } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Copy, Send, Ban, Search, ChevronLeft, ChevronRight, Link2, Award, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND = BACKEND_URL;

const STATUS_STYLES = {
  not_invited: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  invited: "bg-[#33B6E4]/12 text-[#1779AC] dark:text-[#5cc7ef]",
  opened: "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
  activated: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  dormant: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
};

export default function AdminUsers() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [links, setLinks] = useState(null); // invite results modal
  const [cpd, setCpd] = useState(null); // { user, completions, total_hours }
  const [gdprTarget, setGdprTarget] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchRole, setBatchRole] = useState("all");
  const [batchSize, setBatchSize] = useState(500);
  const [batchProgress, setBatchProgress] = useState(null); // {done,total} or null
  const [pendingCount, setPendingCount] = useState(null);

  const loadPending = async (r) => {
    const { data } = await api.get("/admin/invitations/pending-ids", {
      params: { status: "not_invited", role: r === "all" ? undefined : r, limit: 500 },
    });
    setPendingCount(data.total_matching);
  };

  const runBatch = async () => {
    const { data } = await api.get("/admin/invitations/pending-ids", {
      params: { status: "not_invited", role: batchRole === "all" ? undefined : batchRole, limit: Number(batchSize) },
    });
    const ids = data.ids;
    if (!ids.length) return toast.error("No pending users match");
    setBatchProgress({ done: 0, total: ids.length });
    const CHUNK = 50;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      try { await api.post("/admin/invitations/send", { user_ids: chunk }); } catch {}
      setBatchProgress({ done: Math.min(i + CHUNK, ids.length), total: ids.length });
    }
    toast.success(`Sent ${ids.length} invitations`);
    setTimeout(() => { setBatchProgress(null); setBatchOpen(false); load(); loadPending(batchRole); }, 900);
  };

  const gdprDelete = async () => {
    if (!gdprTarget) return;
    try {
      await api.post(`/admin/users/${gdprTarget.id}/gdpr-delete`);
      toast.success("User data anonymised and access revoked");
      setGdprTarget(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const viewCpd = async (uid) => {
    try {
      const { data } = await api.get(`/admin/users/${uid}/cpd`);
      setCpd(data);
    } catch (e) { toast.error("Could not load CPD record"); }
  };

  const load = useCallback(() => {
    api.get("/admin/users", { params: {
      page, search: search || undefined,
      role: role === "all" ? undefined : role,
      status: status === "all" ? undefined : status,
    }}).then(({ data }) => setData(data));
  }, [page, search, role, status]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const invite = async (ids) => {
    if (!ids.length) return toast.error("Select at least one user");
    try {
      const { data } = await api.post("/admin/invitations/send", { user_ids: ids });
      setLinks(data.results);
      setSelected(new Set());
      load();
      const emailed = data.results.filter((r) => r.emailed).length;
      toast.success(emailed ? `${emailed} emailed, ${data.results.length} links ready` : `${data.results.length} invitation links ready`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const revoke = async (ids) => {
    if (!ids.length) return;
    await api.post("/admin/invitations/revoke", { user_ids: ids });
    setSelected(new Set());
    load();
    toast.success("Invitations revoked");
  };

  const totalPages = data ? Math.ceil(data.total / data.page_size) : 1;
  const selectedArr = [...selected];

  return (
    <div className="space-y-5">
      {/* Filters + bulk actions */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="user-search-input"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search name, email, company…"
            className="h-10 w-full rounded-lg border border-input bg-card pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="filter-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="super_admin">Super Admin</SelectItem>
            <SelectItem value="trainer">Trainer</SelectItem>
            <SelectItem value="internal_staff">Internal Staff</SelectItem>
            <SelectItem value="partner">Partner</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Dialog open={batchOpen} onOpenChange={(o) => { setBatchOpen(o); if (o) loadPending(batchRole); }}>
          <DialogTrigger asChild>
            <Button className="btn-brand" data-testid="batch-invite-open"><Send className="mr-1.5 h-4 w-4" /> Batch invite</Button>
          </DialogTrigger>
          <DialogContent data-testid="batch-invite-modal">
            <DialogHeader><DialogTitle>Batch invitations</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Send invitations to not-yet-invited users in a controlled batch.</p>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Audience</label>
                <Select value={batchRole} onValueChange={(v) => { setBatchRole(v); loadPending(v); }}>
                  <SelectTrigger data-testid="batch-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All roles</SelectItem>
                    <SelectItem value="partner">Partners</SelectItem>
                    <SelectItem value="internal_staff">Internal staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Batch size</label>
                <input type="number" min="1" max="500" value={batchSize} onChange={(e) => setBatchSize(e.target.value)}
                  data-testid="batch-size" className="h-10 w-full rounded-lg border border-input bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <span className="tnum font-semibold text-foreground">{pendingCount ?? "…"}</span> users awaiting invitation. This will invite up to <span className="tnum">{batchSize}</span>.
            </div>
            {batchProgress && (
              <div data-testid="batch-progress">
                <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                  <span>Sending…</span><span className="tnum">{batchProgress.done}/{batchProgress.total}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-[#33B6E4] transition-all" style={{ width: `${(batchProgress.done / batchProgress.total) * 100}%` }} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setBatchOpen(false)} disabled={!!batchProgress}>Cancel</Button>
              <Button className="btn-brand" onClick={runBatch} disabled={!!batchProgress || !pendingCount} data-testid="batch-start">
                {batchProgress ? "Sending…" : "Start batch"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#33B6E4]/30 bg-[#33B6E4]/8 px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-medium"><span className="tnum">{selected.size}</span> selected</span>
          <Button size="sm" className="btn-brand" onClick={() => invite(selectedArr)} data-testid="bulk-invite-button">
            <Send className="mr-1.5 h-3.5 w-3.5" /> Send invitations
          </Button>
          <Button size="sm" variant="outline" onClick={() => revoke(selectedArr)} data-testid="bulk-revoke-button">
            <Ban className="mr-1.5 h-3.5 w-3.5" /> Revoke
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="card-surface overflow-hidden">
        <div className="hidden grid-cols-[40px_1fr_140px_130px_1fr] items-center gap-3 border-b border-border bg-secondary/50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
          <div></div><div>Member</div><div>Role</div><div>Status</div><div className="text-right">Actions</div>
        </div>
        <div className="divide-y divide-border">
          {data?.users.map((u) => (
            <div key={u.id} className="grid grid-cols-[40px_1fr] items-center gap-3 px-4 py-3 sm:grid-cols-[40px_1fr_140px_130px_1fr]" data-testid={`user-row-${u.id}`}>
              <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`user-checkbox-${u.id}`} />
              <div className="flex items-center gap-3 min-w-0">
                <Avatar src={u.photo_url} name={u.name} size={36} />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{u.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                </div>
              </div>
              <div className="hidden sm:block"><RoleBadge role={u.role} /></div>
              <div className="hidden sm:block">
                <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_STYLES[u.status])}>{STATUS_LABELS[u.status]}</span>
              </div>
              <div className="col-span-2 flex justify-end gap-1.5 sm:col-span-1">
                <Button size="sm" variant="ghost" onClick={() => viewCpd(u.id)} data-testid={`cpd-user-${u.id}`} title="CPD record">
                  <Award className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => invite([u.id])} data-testid={`invite-user-${u.id}`}>
                  <Send className="h-3.5 w-3.5" />
                </Button>
                {["invited", "opened"].includes(u.status) && (
                  <Button size="sm" variant="ghost" onClick={() => revoke([u.id])} data-testid={`revoke-user-${u.id}`}>
                    <Ban className="h-3.5 w-3.5" />
                  </Button>
                )}
                {u.status !== "deleted" && u.role !== "super_admin" && (
                  <Button size="sm" variant="ghost" onClick={() => setGdprTarget(u)} data-testid={`gdpr-user-${u.id}`} title="GDPR delete">
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {data && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="tnum">{data.total} users</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="tnum">Page {page} / {Math.max(1, totalPages)}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Invite links modal */}
      <Dialog open={!!links} onOpenChange={(o) => !o && setLinks(null)}>
        <DialogContent className="max-w-lg" data-testid="invite-links-modal">
          <DialogHeader><DialogTitle>Invitation links</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Copy and share these one-click magic links. They expire in 7 days and can be used once.
          </p>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {links?.map((r) => (
              <div key={r.user_id} className="rounded-lg border border-border p-3">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{r.email}</span>
                  {r.emailed && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">Emailed</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <input readOnly value={r.link} className="flex-1 truncate rounded bg-secondary px-2 py-1 text-xs" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(r.link); toast.success("Copied"); }} data-testid={`copy-link-${r.user_id}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinks(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Per-user CPD record modal */}
      <Dialog open={!!cpd} onOpenChange={(o) => !o && setCpd(null)}>
        <DialogContent className="max-w-lg" data-testid="user-cpd-modal">
          <DialogHeader><DialogTitle>CPD record for {cpd?.user?.name}</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{cpd?.user?.company} · {cpd?.user?.email}</span>
            <span className="tnum font-semibold">{cpd?.total_hours} hrs</span>
          </div>
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {cpd?.completions?.length ? cpd.completions.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{c.course_title}</div>
                  <div className="text-xs text-muted-foreground tnum">{c.cpd_hours} hrs · {c.score}% · {c.verification_ref}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => window.open(`${BACKEND}/api/certificates/${c.id}.pdf`, "_blank")}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </div>
            )) : <p className="py-6 text-center text-sm text-muted-foreground">No completions yet.</p>}
          </div>
          <DialogFooter>
            {cpd?.user && (
              <Button variant="outline" onClick={() => window.open(`${BACKEND}/api/admin/users/${cpd.user.id}/cpd/export.csv`, "_blank")} data-testid="export-user-cpd-csv">
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
            )}
            <Button onClick={() => setCpd(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* GDPR delete confirm */}
      <Dialog open={!!gdprTarget} onOpenChange={(o) => !o && setGdprTarget(null)}>
        <DialogContent data-testid="gdpr-confirm-modal">
          <DialogHeader><DialogTitle>Delete &amp; anonymise this user?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently removes personal data for <strong>{gdprTarget?.name}</strong> ({gdprTarget?.email}):
            name, email, company and profile are erased, their password and sessions are revoked, and they are
            removed from directories. CPD completion records are retained but anonymised. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGdprTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={gdprDelete} data-testid="confirm-gdpr-delete">Delete &amp; anonymise</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
